import { Component, EnvironmentInjector, inject } from '@angular/core';
import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular/standalone';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PermissionService } from './common/services/permission.service';

import { filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '../environments/environment';
import { initializeApp } from 'firebase/app';
import { LanguageService } from './common/services/language.service';
import { FirebaseNotificationService } from './common/services/firebase-notification.service';

initializeApp(environment.firebaseConfig);

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: true,
  imports: [IonApp, IonRouterOutlet, RouterModule, TranslateModule],
})
export class AppComponent {
  environmentInjector = inject(EnvironmentInjector);
  isContactPage = false;

  constructor(
    private platform: Platform,
    private router: Router,
    private languageService: LanguageService,
    private firebaseNotification: FirebaseNotificationService,
    private permissions: PermissionService,
  ) {
    this.platform.ready().then(() => {
      this.initializeStatusBarSafe();
      // ask for camera / photos / location once, on first launch
      this.permissions.ensureFirstRun();
      this.firebaseNotification.init();
      this.initializeDeepLinks();
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        this.isContactPage = url.split('?')[0].split('#')[0].endsWith('/tabs/contact');

        // A soil-card QR was scanned before login: once the user is inside the
        // app (logged in, on any tab), take them to their card
        if (url.startsWith('/tabs')
          && localStorage.getItem('pendingSoilCard')
          && localStorage.getItem('authToken')
          && !url.includes('/soil-nutrient')) {
          this.router.navigate(['/tabs/settings/soil-nutrient']);
        }
      });
  }

  // Handles uzhavar://soil-card?district=..&block=..&village=..&surveyNumber=..
  // coming from the soil health card QR code
  private initializeDeepLinks() {
    if (!Capacitor.isNativePlatform()) return;

    CapacitorApp.addListener('appUrlOpen', (event) => {
      try {
        const url = event?.url || '';
        if (!url.includes('soil-card')) return;

        const qs = url.split('?')[1] || '';
        const params: any = {};
        new URLSearchParams(qs).forEach((v, k) => (params[k] = v));
        if (!params.district || !params.block || !params.village || !params.surveyNumber) return;

        localStorage.setItem('pendingSoilCard', JSON.stringify(params));

        if (localStorage.getItem('authToken')) {
          this.router.navigate(['/tabs/settings/soil-nutrient']);
        } else {
          // must login first — the pending card opens right after
          this.router.navigate(['/login']);
        }
      } catch (e) {
        console.log('deep link parse failed', e);
      }
    });
  }

  async initializeStatusBarSafe() {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await StatusBar.setOverlaysWebView({ overlay: true });
      // The plugin only accepts a hex string — it was being passed
      // 'rgba(129, 199, 132, 0.19)' and rejected it on every launch with
      // "Invalid color provided. Must be a hex string". #81C784 is the same
      // colour. (With overlay:true the bar is transparent anyway, so this is
      // only the fallback for when the overlay is turned off.)
      await StatusBar.setBackgroundColor({ color: '#81C784' });
      await StatusBar.setStyle({ style: Style.Light });
    } catch (e) {
      // ignore
    }
  }
}
